import type en from '../en/users'
import type { Translation } from '../types'

/** People, roles and permissions */
const users: Translation<typeof en> = {
  "users.removed": "Utilisateur supprimé",
  "users.removeFailed": "L’utilisateur n’a pas pu être supprimé",
  "users.vpnUpdated": "Statut VPN mis à jour",
  "users.vpnFailed": "Le VPN n’a pas pu être modifié",
  "users.inviteSent": "Invitation envoyée",
  "users.inviteSentAgain": "Un nouvel e-mail d’invitation a été envoyé.",
  "users.resendFailed": "L’invitation n’a pas pu être renvoyée",
  "users.reset2faDone": "Connexion à deux facteurs réinitialisée",
  "users.reset2faText": "La personne est déconnectée partout et la reconfigure à sa prochaine connexion.",
  "users.resetFailed": "Échec de la réinitialisation",
  "users.title": "Utilisateurs",
  "users.count": { one: "{count} membre de l’équipe", other: "{count} membres de l’équipe" },
  "users.invite": "Inviter un utilisateur",
  "users.search": "Rechercher des utilisateurs",
  "users.searchPlaceholder": "Rechercher par nom ou e-mail…",
  "users.user": "Utilisateur",
  "users.role": "Rôle",
  "users.lastLogin": "Dernière connexion",
  "users.empty": "Aucun utilisateur trouvé",
  "users.adminBadge": "Admin",
  "users.twoFactorOn": "La connexion à deux facteurs est activée",
  "users.never": "Jamais",
  "users.resend": "Renvoyer l’e-mail d’invitation",
  "users.suspendVpn": "Suspendre l’accès VPN",
  "users.enableVpn": "Réactiver l’accès VPN",
  "users.suspendVpnConfirm": "Suspendre le VPN de « {name} » ?",
  "users.enableVpnConfirm": "Réactiver le VPN de « {name} » ?",
  "users.reset2fa": "Réinitialiser la connexion à deux facteurs",
  "users.reset2faFor": "Réinitialiser la connexion à deux facteurs de {name}",
  "users.reset2faConfirm": "Réinitialiser la connexion à deux facteurs de « {name} » ? La personne est déconnectée partout et la reconfigure à sa prochaine connexion.",
  "users.editAccess": "Modifier l’accès",
  "users.removeNamed": "Supprimer {name}",
  "users.removeConfirm": "Supprimer l’utilisateur « {name} » ?",
  "users.updated": "Utilisateur mis à jour",
  "users.addedNoMail": "Utilisateur ajouté, mais l’e-mail d’invitation n’a pas pu être envoyé",
  "users.addedNoMailText": "Vérifiez les paramètres de messagerie, puis utilisez « Renvoyer l’e-mail d’invitation ».",
  "users.siteAccess": "Accès aux sites",
  "users.allSites": "Tous les sites (y compris ceux ajoutés plus tard)",
  "users.noSites": "Aucun site pour l’instant",
  "users.noneSelected": "Aucun site choisi — cet utilisateur ne verra aucun site.",
  "users.updateFailed": "L’utilisateur n’a pas pu être mis à jour",
  "users.editTitle": "Modifier {name}",
  "users.noRole": "Aucun rôle",
  "users.noRoleCustom": "Aucun rôle (droits propres)",
  "users.companyAdmin": "Administrateur de l’entreprise (accès complet)",
  "users.save": "Enregistrer",
  "users.inviteFailed": "L’utilisateur n’a pas pu être invité",
  "users.inviteTitle": "Inviter un membre de l’équipe",
  "users.fullName": "Nom complet *",
  "users.fullNamePlaceholder": "Marie Dupont",
  "users.email": "Adresse e-mail *",
  "users.emailPlaceholder": "marie@entreprise.fr",
  "users.inviteInfo": "Un e-mail d’invitation est envoyé avec un lien pour définir un mot de passe. Le compte VPN est créé automatiquement.",
  "users.sendInvite": "Envoyer l’invitation",
  "users.roles.deleted": "Rôle supprimé",
  "users.roles.deleteFailed": "Le rôle n’a pas pu être supprimé",
  "users.roles.title": "Rôles",
  "users.roles.subtitle": "Ensembles de droits pour les membres de votre équipe",
  "users.roles.create": "Créer un rôle",
  "users.roles.system": "SYSTÈME",
  "users.roles.editNamed": "Modifier {name}",
  "users.roles.deleteNamed": "Supprimer {name}",
  "users.roles.deleteConfirm": "Supprimer le rôle « {name} » ?",
  "users.roles.empty": "Aucun rôle pour l’instant — créez-en un pour l’attribuer aux membres de l’équipe",
  "users.roles.noPermissions": "Aucun droit attribué",
  "users.roles.updated": "Rôle mis à jour",
  "users.roles.updateFailed": "Le rôle n’a pas pu être mis à jour",
  "users.roles.savePermissions": "Enregistrer les droits",
  "users.roles.created": "Rôle créé",
  "users.roles.createFailed": "Le rôle n’a pas pu être créé",
  "users.roles.name": "Nom du rôle *",
  "users.roles.namePlaceholder": "Ingénieur réseau",
  "users.roles.description": "Description",
  "users.roles.descriptionPlaceholder": "Description facultative",
  "users.roles.permissions": "Droits",
  "users.roles.selected": { one: "{count} droit choisi", other: "{count} droits choisis" },
  "users.roles.hoverHint": "Survolez un droit pour voir ce qu’il permet",
  "users.group.sites": "Sites",
  "users.group.monitoring": "Supervision",
  "users.group.templates": "Modèles",
  "users.group.users": "Utilisateurs",
  "users.group.vpn": "VPN",
  "users.group.company": "Entreprise",
  "users.group.api": "API",
  "users.permission.SITE_VIEW": "Voir",
  "users.permission.SITE_CREATE": "Créer",
  "users.permission.SITE_EDIT": "Modifier",
  "users.permission.SITE_DELETE": "Supprimer",
  "users.permission.MONITORING_VIEW": "Voir",
  "users.permission.TEMPLATE_VIEW": "Voir",
  "users.permission.TEMPLATE_CREATE": "Créer",
  "users.permission.TEMPLATE_EDIT": "Modifier",
  "users.permission.TEMPLATE_DELETE": "Supprimer",
  "users.permission.TEMPLATE_APPLY": "Appliquer",
  "users.permission.USER_VIEW": "Voir",
  "users.permission.USER_CREATE": "Inviter",
  "users.permission.USER_EDIT": "Modifier",
  "users.permission.USER_DELETE": "Retirer",
  "users.permission.VPN_ACCESS": "Accès",
  "users.permission.VPN_VIEW": "Voir",
  "users.permission.VPN_MANAGE": "Gérer",
  "users.permission.COMPANY_BRANDING": "Identité visuelle",
  "users.permission.COMPANY_SETTINGS": "Paramètres",
  "users.permission.LICENSE_VIEW": "Voir la licence",
  "users.permission.LICENSE_MANAGE": "Gérer la licence",
  "users.permission.APIKEY_VIEW": "Voir",
  "users.permission.APIKEY_CREATE": "Créer",
  "users.permission.APIKEY_DELETE": "Révoquer",
  "users.permissionDesc.SITE_VIEW": "Voir la liste des sites et leurs informations de base",
  "users.permissionDesc.SITE_CREATE": "Ajouter de nouveaux sites",
  "users.permissionDesc.SITE_EDIT": "Modifier les paramètres et la clé API d’un site",
  "users.permissionDesc.SITE_DELETE": "Supprimer définitivement des sites",
  "users.permissionDesc.MONITORING_VIEW": "Voir les données en direct, les appareils et les clients",
  "users.permissionDesc.TEMPLATE_VIEW": "Parcourir la bibliothèque de modèles",
  "users.permissionDesc.TEMPLATE_CREATE": "Créer de nouveaux modèles",
  "users.permissionDesc.TEMPLATE_EDIT": "Modifier des modèles existants",
  "users.permissionDesc.TEMPLATE_DELETE": "Supprimer des modèles",
  "users.permissionDesc.TEMPLATE_APPLY": "Appliquer des modèles aux sites",
  "users.permissionDesc.USER_VIEW": "Voir la liste des membres de l’équipe",
  "users.permissionDesc.USER_CREATE": "Inviter de nouveaux membres",
  "users.permissionDesc.USER_EDIT": "Modifier les rôles et le statut",
  "users.permissionDesc.USER_DELETE": "Retirer des membres de l’entreprise",
  "users.permissionDesc.VPN_ACCESS": "Le membre reçoit un compte VPN",
  "users.permissionDesc.VPN_VIEW": "Voir la page VPN et l’état des comptes",
  "users.permissionDesc.VPN_MANAGE": "Créer, suspendre et renouveler des comptes VPN",
  "users.permissionDesc.COMPANY_BRANDING": "Modifier le logo, les couleurs et l’apparence",
  "users.permissionDesc.COMPANY_SETTINGS": "Gérer les paramètres et le serveur de messagerie de l’entreprise",
  "users.permissionDesc.LICENSE_VIEW": "Voir l’état et les limites de la licence",
  "users.permissionDesc.LICENSE_MANAGE": "Renouveler et modifier la licence",
  "users.permissionDesc.APIKEY_VIEW": "Voir la liste des clés API",
  "users.permissionDesc.APIKEY_CREATE": "Créer de nouvelles clés API",
  "users.permissionDesc.APIKEY_DELETE": "Révoquer des clés API",
  "users.group.rental": "Location",
  "users.permission.RENTAL_VIEW": "Voir",
  "users.permission.RENTAL_PLAN": "Planifier",
  "users.permission.STOCK_MANAGE": "Gérer le stock",
  "users.permission.PURCHASE_APPROVE": "Approuver et envoyer les achats",
  "users.permissionDesc.RENTAL_VIEW": "Voir le stock, les clients, les événements, la planification et les prix de location",
  "users.permissionDesc.RENTAL_PLAN": "Gérer les clients et les événements : lignes, prix, remises et statut",
  "users.permissionDesc.STOCK_MANAGE": "Gérer les articles, les pièces, les caisses, les entrepôts, les transferts, les fournisseurs, les sous-locations et les bons de commande, et voir les coûts",
  "users.permissionDesc.PURCHASE_APPROVE": "Approuver les bons de commande et les demandes de sous-location, ce qui les envoie au fournisseur",
  "users.permission.MIKROTIK_PROVISION": "Provisionner des unités MikroTik",
  "users.permissionDesc.MIKROTIK_PROVISION": "Créer ou mettre à jour un site MikroTik par son numéro de série, délivrer son certificat VPN et recevoir sa configuration",
  "users.permission.TELTONIKA_PROVISION": "Provisionner des routeurs Teltonika",
  "users.permissionDesc.TELTONIKA_PROVISION": "Créer ou mettre à jour un site Teltonika par son numéro de série, délivrer son certificat VPN et recevoir sa configuration",
  "users.permission.CLIENT_ACCOUNTS_MANAGE": "Gérer les comptes clients",
  "users.permissionDesc.CLIENT_ACCOUNTS_MANAGE": "Inviter, modifier et retirer les personnes d’un client, et ouvrir « Voir comme le client » sur un événement. Uniquement pour les membres ayant accès à tous les sites",
}

export default users
